import { API, Storage } from 'aws-amplify';
import { v5 as uuidv5 } from 'uuid';
import { getStatusKeyByLabel } from '../../src/utils/resources';
import { APP_AUTH_MODE } from '../constants/app';
import { EXECUTION_TYPES, PROCESS_TYPES, RESOURCE_STATUS, RESOURCE_TYPES } from '../constants/status';
import {
  createExternalResource as createExternalResourceCmd,
  createInternalResource as createInternalResourceCmd,
  updateExternalResource,
  updateInternalResource,
  processExternalResource as processExternalResourceCmd,
  processInternalResource as processInternalResourceCmd,
  deleteExternalResource as deleteExternalResourceCmd,
  deleteInternalResource as deleteInternalResourceCmd,
} from '../graphql/mutations';
import {
  findExternalResourcesSortByType,
  findInternalResourcesSortByType, getExternalResource as getExternalResourceCmd, listExternalResources as listExternalResourcesCmd,
  listInternalResources as listInternalResourcesCmd
} from '../graphql/queries';

const listResources = async (governmentId, type=null, isExternal=true, nextToken=null, selectedStatus, selectedDepartment, keyword=null) => {
  const query = isExternal?
    (type? findExternalResourcesSortByType: listExternalResourcesCmd):
    (type? findInternalResourcesSortByType: listInternalResourcesCmd);
  const property = isExternal?
    (type? 'findExternalResourcesSortByType': 'listExternalResources'):
    (type? 'findInternalResourcesSortByType': 'listInternalResources');
  const params = {
    sortDirection: 'DESC',
    nextToken
  };
  params.governmentId = governmentId;
  if (type) {
    params.typeCreatedAt = {beginsWith: {type, createdAt: ''}};
  }

  const selectedStatusKey = getStatusKeyByLabel(selectedStatus)

  params.filter = {};

  if (selectedStatusKey) {
    params.filter.status = { eq: selectedStatusKey };
  }

  if (selectedDepartment) {
    params.filter.tag = { eq: selectedDepartment };
  }

  if (keyword && keyword.trim() !== "") {
    params.filter.name = { contains: keyword.trim() };
  }

  if (Object.keys(params.filter).length === 0) {
    delete params.filter;
  }

  try {
    const res = await API.graphql({
      query,
      variables: params,
      authMode: APP_AUTH_MODE
    })
    if (res.data[property]) {
      const data = res.data[property];
      const items = data.items;
      for (const item of items) {
        if (item.executionDetail) {
          item.executionDetail = JSON.parse(item.executionDetail);
        }
      }
      return {
        success: true,
        data: items,
        nextToken: data.nextToken
      }
    }
    return {
      success: false,
      error: res.errors
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const listFiles = async(governmentId, isExternal=true, nextToken=null) => {
  return await listResources(governmentId, RESOURCE_TYPES.FILE, isExternal, nextToken);
}

const listFaqs = async (governmentId, isExternal, nextToken=null, keyword=null) => {
  return await listResources(governmentId, 'FAQ', isExternal, nextToken, null, null, keyword);
}

const _listAllFaqsInSelectedTime = async (governmentId, selectedTime, isExternal) => {
  const query = isExternal? findExternalResourcesSortByType: findInternalResourcesSortByType;
  const property = isExternal? 'findExternalResourcesSortByType': 'findInternalResourcesSortByType';
  let nextToken = null;
  const params = {
    sortDirection: 'DESC',
    nextToken,
    governmentId,
    typeCreatedAt: {
      beginsWith: {
        type: 'FAQ',
        createdAt: selectedTime
      }
    }
  };
  const items = [];
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      params.nextToken = nextToken;
      const res = await API.graphql({
        query,
        variables: params,
        authMode: APP_AUTH_MODE
      });
      items.push(...res.data[property].items);
      nextToken = res.data[property].nextToken;
      if (!nextToken) break;
    }
    return {
      success: true,
      data: items
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const listAllInternalFaqsInSelectedTime = async (governmentId, selectedTime) => {
  return await _listAllFaqsInSelectedTime(governmentId, selectedTime, false)
}

const listAllExternalFaqsInSelectedTime = async (governmentId, selectedTime) => {
  return await _listAllFaqsInSelectedTime(governmentId, selectedTime, true)
}

const listAllFaqsWithKeyword = async (governmentId, isExternal, keyword=null, selectedStatus=null, selectedDepartment=null) => {
  const query = isExternal? findExternalResourcesSortByType: findInternalResourcesSortByType;
  const property = isExternal? 'findExternalResourcesSortByType': 'findInternalResourcesSortByType';
  let nextToken = null;
  const params = {
    sortDirection: 'DESC',
    nextToken,
    governmentId,
    typeCreatedAt: {
      beginsWith: {
        type: 'FAQ',
        createdAt: ''
      }
    }
  };

  const selectedStatusKey = getStatusKeyByLabel(selectedStatus);
  params.filter = {};

  if (selectedStatusKey) {
    params.filter.status = { eq: selectedStatusKey };
  }

  if (selectedDepartment) {
    params.filter.tag = { eq: selectedDepartment };
  }

  if (keyword && keyword.trim() !== "") {
    params.filter.name = { contains: keyword.trim() };
  }

  if (Object.keys(params.filter).length === 0) {
    delete params.filter;
  }

  const items = [];
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      params.nextToken = nextToken;
      const res = await API.graphql({
        query,
        variables: params,
        authMode: APP_AUTH_MODE
      });

      const data = res.data[property];
      if (data && data.items) {
        for (const item of data.items) {
          if (item.executionDetail) {
            item.executionDetail = JSON.parse(item.executionDetail);
          }
        }
        items.push(...data.items);
      }

      nextToken = data?.nextToken;
      if (!nextToken) break;
    }
    return {
      success: true,
      data: items
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _createResource = async (id, governmentId, name, answer, url, srcMsgKey, type, isExternal=true, tag) => {
  try {
    const params = {
      governmentId,
      name,
      url,
      type,
      status: type===RESOURCE_TYPES.FAQ || type===RESOURCE_TYPES.FILE? RESOURCE_STATUS.PENDING: RESOURCE_STATUS.PROCESS_REQUESTING,
      srcMsgKey,
      process: PROCESS_TYPES.CREATE,
      execution: EXECUTION_TYPES.IMMEDIATE,
      tag: tag,
    };
    if (type===RESOURCE_TYPES.WEBSITE) {
      params.id = uuidv5(name, uuidv5.URL);
    }
    if (id) {
      params.id = id;
    }
    if (answer) params.answer = answer;
    const res = await API.graphql({
      query: isExternal? createExternalResourceCmd: createInternalResourceCmd,
      variables: {
        input: params
      },
      authMode: APP_AUTH_MODE
    });
    const data = res.data[isExternal? 'createExternalResource': 'createInternalResource'];
    if (!data) {
      return {
        success: false,
        error: res.errors
      }
    }
    return {
      success: true,
      data: data
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const _updateResource = async (governmentId, createdAt, type, newProps, isExternal=true) => {
  try {
    const res = await API.graphql({
      query: isExternal? updateExternalResource: updateInternalResource,
      variables: {
        input: {
          governmentId,
          createdAt,
          type,
          ...newProps
        }
      },
      authMode: APP_AUTH_MODE
    });
    const data = res.data[isExternal? 'updateExternalResource': 'updateInternalResource'];
    if (!data) {
      return {
        success: false,
        error: res.errors
      }
    }
    return {
      success: true,
      data: data
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const updateResource = async (resource, newProps={}) => {
  try {
    const newResource = Object.assign({}, resource, newProps);
    const isExternal = resource.__typename === 'ExternalResource';
    delete newResource.updatedAt;
    delete newResource.__typename;
    if (newResource.executionDetail) {
      newResource.executionDetail = JSON.stringify(newResource.executionDetail);
    }
    const res = await API.graphql({
      query: isExternal? updateExternalResource: updateInternalResource,
      variables: {
        input: newResource
      },
      authMode: APP_AUTH_MODE
    });
    const data = res.data[isExternal? 'updateExternalResource': 'updateInternalResource'];

    if (!data) {
      return {
        success: false,
        error: res.errors
      }
    }
    if (data.executionDetail) {
      data.executionDetail = JSON.parse(data.executionDetail);
    }
    return {
      success: true,
      data: data
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}


const createFile = async (id, governmentId, name, isExternal) => {
  return await _createResource(id, governmentId, name, null, null, null, 'FILE', isExternal);
}

const createFaq = async (governmentId, name, answer, url=null, srcMsgKey=null, isExternal=true, tag=null) => {
  return await _createResource(null, governmentId, name, answer, url, srcMsgKey, 'FAQ', isExternal, tag);
}

// eslint-disable-next-line no-underscore-dangle
const getExternalResource = async (governmentId, createdAt) => {
  const query = getExternalResourceCmd;
  const property = "getExternalResource";
  try {
    const res = await API.graphql({
      query,
      variables: {
        governmentId,
        createdAt,
      },
      authMode: APP_AUTH_MODE,
    });
    if (res.data[property]) {
      return {
        success: true,
        data: res.data[property],
      };
    }
    return {
      success: false,
      error: res.errors,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
    };
  }
};



const processResource = async (resource) => {
  try {
    const isExternal = resource.__typename === 'ExternalResource';
    const query = isExternal? processExternalResourceCmd: processInternalResourceCmd;
    const property = isExternal? 'processExternalResource': 'processInternalResource';
    const res = await API.graphql({
      query,
      variables: {
        input: {
          type: resource.type,
          governmentId: resource.governmentId,
          createdAt: resource.createdAt
        }
      },
      authMode: APP_AUTH_MODE
    })
    if (res.data) {
      return res.data[property];
    }
    return {
      success: false,
      error: res.errors
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}
const startProcessingResource = async (resource) => {
  if (resource.execution===EXECUTION_TYPES.SCHEDULED || resource.execution===EXECUTION_TYPES.RECURRING) {
    return await updateResource(resource, {status: RESOURCE_STATUS.WAITING, process: PROCESS_TYPES. CREATE});
  }
  return await updateResource(resource, {status: RESOURCE_STATUS.PROCESSING, process: PROCESS_TYPES. CREATE});
}
const requestProcessingResource = async (resource) => {
  return await updateResource(resource, {
    status: RESOURCE_STATUS.PROCESS_REQUESTING,
    rejectReason: ''
  })
}
const requestDeletingResource = async (resource) => {
  return await updateResource(resource, {
    status: RESOURCE_STATUS.DELETE_REQUESTING,
    process: PROCESS_TYPES.DELETE,
    rejectReason: ''
  })
}
const requestReprocessingResource = async (resource) => {
  return await updateResource(resource, {status: RESOURCE_STATUS.REPROCESS_REQUESTING, rejectReason: ''});
}
const rejectProcessingResource = async (resource, rejectReason) => {
  if (resource.type!==RESOURCE_TYPES.FILE) {
    return await updateResource(resource, {status: RESOURCE_STATUS.PROCESS_REJECTED, rejectReason});
  }
  return {
    success: false,
    error: 'Cannot reject processing a file'
  }
}
const rejectDeletingResource = async (resource, rejectReason) => {
  return await updateResource(resource,
    {
      status: RESOURCE_STATUS.FINISHED,
      process: PROCESS_TYPES.CREATE,
      rejectReason
    }
  );
}
const startDeletingResource = async (resource) => {
  if (resource.execution===EXECUTION_TYPES.SCHEDULED || resource.execution===EXECUTION_TYPES.RECURRING) {
    return await updateResource(resource, {status: RESOURCE_STATUS.WAITING, process: PROCESS_TYPES. DELETE});
  }
  return await updateResource(resource, {status: RESOURCE_STATUS.PROCESSING, process: PROCESS_TYPES.DELETE});
}
const adminDeleteResource = async (resource) => {
  return await processResource(resource);
}
const deleteResource = async (resource) => {
  try {
    const isExternal = resource.__typename==='ExternalResource';
    const input = {
      governmentId: resource.governmentId,
      createdAt: resource.createdAt
    };
    const res = await API.graphql({
      query: isExternal ? deleteExternalResourceCmd : deleteInternalResourceCmd,
      variables: { input },
      authMode: APP_AUTH_MODE,
    });
    const data = res?.data[isExternal ? 'deleteExternalResource' : 'deleteInternalResource'];
    if (!data) {
      return {
        success: false,
        error: res?.errors,
      };
    }
    if (data.type==='FILE') {
      const extension = data.name.substr(data.name.lastIndexOf('.'));
      await Storage.remove(`${resource.governmentId}/${isExternal? 'external': 'internal'}/file/${data.id}${extension}`);
    }
    return {
      success: true,
      data: data,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
    };
  }
}
const updateExecution = async (resource, execution, executionDetail) => {
  delete resource.updatedAt;
  resource.execution = execution;
  resource.executionDetail = JSON.stringify(executionDetail);
  const isExternal = resource.__typename=='ExternalResource';
  delete resource.__typename;
  return await _updateResource(resource.governmentId, resource.createdAt, resource.type, resource, isExternal);
}

const getFaqById = async (governmentId, faqId) => {
  const query = findExternalResourcesSortByType;
  const property = 'findExternalResourcesSortByType';

  const params = {
    governmentId,
    sortDirection: 'DESC',
    typeCreatedAt: {
      beginsWith: {
        type: 'FAQ',
        createdAt: ''
      }
    },
    filter: {
      id: { eq: faqId }
    }
  };

  try {
    const res = await API.graphql({
      query,
      variables: params,
      authMode: APP_AUTH_MODE
    });

    if (res.data[property] && res.data[property].items && res.data[property].items.length > 0) {
      const item = res.data[property].items[0];
      if (item.executionDetail) {
        item.executionDetail = JSON.parse(item.executionDetail);
      }
      return {
        success: true,
        data: item
      };
    }

    return {
      success: false,
      error: 'FAQ not found'
    };
  }
  catch (e) {
    return {
      success: false,
      error: e
    };
  }
};

const getExternalFaqById = async (governmentId, faqId) => {
  return await getFaqById(governmentId, faqId);
};

export {
  listResources,
  listFiles,
  listFaqs,
  listAllFaqsWithKeyword,
  listAllExternalFaqsInSelectedTime,
  listAllInternalFaqsInSelectedTime,
  createFaq,
  createFile,
  getExternalResource,
  getExternalFaqById,
  processResource,
  startProcessingResource,
  requestProcessingResource,
  requestDeletingResource,
  requestReprocessingResource,
  rejectProcessingResource,
  rejectDeletingResource,
  startDeletingResource,
  adminDeleteResource,
  deleteResource,
  updateResource,
  updateExecution
};
